import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Container } from "react-bootstrap"
import { getJobServicesDrafting, getDraftsmenList } from "requesters/jobServiceRequester"
import { getDraftmen } from "requesters/userRequester"
import { getFilteredProducts } from "requesters/productRequester"
import CollapsibleSection from "components/CollapsibleSection"
import {
  DRAFTING_OVERSEAS_MANAGER,
  DRAFTING_OVERSEAS,
  DRAFTING_MANAGER,
  DRAFTING_3D_MANAGER,
  TO_OVERSEAS_DRAFTING,
  DRAFTING_OVERSEAS_USER,
  DRAFTING_3D_OVERSEAS_LEBANON_MANAGER,
  DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER,
  DRAFTING_OVERSEAS_LEBANON,
  DRAFTING_OVERSEAS_PHILIPPINES,
  DRAFTING_3D_OVERSEAS_LEBANON_USER,
  DRAFTING_3D_OVERSEAS_PHILIPPINES_USER,
  DRAFTING_3D,
  DRAFTING,
  AWAITING_CONTROL_SURVEY,
  OVERSEAS_STATE
} from "utils/constants"
import { roleCheck } from "utils/index"
import SearchInput from "components/SearchInput"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import AnchorButton from "components/AnchorButton"
import UserBlockOutTable from "components/UserBlockOutTable"
import _ from "lodash"
import useJobServicesListRealtime, { applyJobServiceListUrgencyUpdate } from "../../hooks/useJobServicesListRealtime"
import DraftingTable from "./DraftingTable"
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../reducers"

const teamShouldShowLeave = [DRAFTING, DRAFTING_3D, DRAFTING_OVERSEAS, DRAFTING_OVERSEAS_PHILIPPINES, DRAFTING_OVERSEAS_LEBANON]

const draftsmanKeyFor = (key, name) => String(key || name)

const hasCustomFilters = (filters, defaultFilters) => (
  Object.entries(filters).some(([key, val]) => key !== "by_draftman" && val !== defaultFilters[key])
)

export default function DraftingPage() {
  const baseUrl = useSelector(getBaseUrl)
  const currentRole = useSelector(getCurrentRole)
  const currentUser = useSelector(getCurrentUser)
  const systemRole = currentUser.attributes.role
  const currentTeamRole = currentUser.attributes.current_team_role
  const readOnly = !(roleCheck(systemRole, currentTeamRole, DRAFTING_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_LEBANON_MANAGER))

  const liveJobsAllowed = () => {
    if (roleCheck(systemRole, currentRole, DRAFTING) || roleCheck(systemRole, currentRole, DRAFTING_3D) || roleCheck(systemRole, currentRole, DRAFTING_OVERSEAS) || roleCheck(systemRole, currentRole, DRAFTING_OVERSEAS_LEBANON) || roleCheck(systemRole, currentRole, DRAFTING_OVERSEAS_PHILIPPINES)) {
      return true
    }
    return false
  }

  if (!liveJobsAllowed()) window.location = "/"

  const [requesting, setRequesting] = useState(true)
  const [jobsDrafting, setJobsDrafting] = useState({})
  const [draftmenOptions, setDraftmenOptions] = useState([])
  const [draftsmenList, setDraftsmenList] = useState([])
  const [loadingDraftsmen, setLoadingDraftsmen] = useState({})
  const jobStatusOptions = [
    ["Drafting Required", "drafting_assignment_required"],
    ["QA Required", "local_qa"],
    ["Overseas", "overseas_drafting_assignment_required"],
    ["Overseas QA", "overseas_qa"],
    ["Report Required", "report_required"],
    ["Drafting Overseas Required", "to_overseas_drafting"],
    ["Local Drafting", "to_local_drafting"],
  ]

  const [jobTypes, setJobTypes] = useState([])

  const isOverseasTeam = currentRole?.includes(DRAFTING_OVERSEAS) || currentRole?.includes(DRAFTING_OVERSEAS_LEBANON) || currentRole?.includes(DRAFTING_OVERSEAS_PHILIPPINES)
  const isOverseasManager = currentTeamRole.includes(DRAFTING_OVERSEAS_MANAGER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_LEBANON_MANAGER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER)
  const isOverseasUser = currentTeamRole.includes(DRAFTING_OVERSEAS_USER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_LEBANON_USER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_PHILIPPINES_USER)
  const isLocalDrafting = currentUser.attributes.default_team === "drafting"
  const is2DTeam = currentRole.includes(DRAFTING)
  const isManagerRole = roleCheck(systemRole, currentTeamRole, DRAFTING_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_MANAGER) || isOverseasManager

  const defaultFilters = useMemo(() => ({
    by_status: (isOverseasUser && !isLocalDrafting) ? TO_OVERSEAS_DRAFTING : undefined,
    by_draftman: isOverseasManager ? undefined : (readOnly ? currentUser.id : undefined),
    by_job_type: undefined,
    by_keyword: undefined
  }), [isOverseasUser, isLocalDrafting, isOverseasManager, readOnly, currentUser.id])

  const [filters, setFilters] = useState(defaultFilters)
  const [expandedDraftsmen, setExpandedDraftsmen] = useState({})
  const [isFilteredByDropDown, setIsFilteredByDropDown] = useState(false)
  const fetchIdRef = useRef(0)
  const byStatusName = _.get(jobStatusOptions, `${_.findIndex(jobStatusOptions, option => option[1] === filters.by_status)}.0`)
  const byJobTypeName = _.get(jobTypes, `${_.findIndex(jobTypes, type => type[1] === filters.by_job_type)}.0`)
  const byDraftmenName = _.get(draftmenOptions, `${_.findIndex(draftmenOptions, option => option[1] === filters.by_draftman)}.0`)
  const filtersApplied = hasCustomFilters(filters, defaultFilters)

  const clearFilters = () => {
    setFilters(defaultFilters)
    setIsFilteredByDropDown(true)
  }

  const setByStatus = val => {
    setFilters({ ...filters, by_status: val })
    setIsFilteredByDropDown(true)
  }

  const setByJobType = val => {
    setFilters({ ...filters, by_job_type: val })
    setIsFilteredByDropDown(true)
  }

  const setBySearch = val => {
    setFilters({ ...filters, by_keyword: val })
  }

  const setByDraftmen = val => {
    setFilters({ ...filters, by_draftman: val })
    setIsFilteredByDropDown(true)
  }

  const fetchDataForDraftsman = useCallback((draftsmanKey, draftsmanId, { silent, fetchId, filters: activeFilters } = {}) => {
    const requestId = fetchId ?? fetchIdRef.current
    const filtersToUse = activeFilters ?? filters

    if (!silent) setLoadingDraftsmen(prev => ({ ...prev, [draftsmanKey]: true }))

    let draftsmanFilter
    if (draftsmanKey === "Awaiting Control Survey") {
      draftsmanFilter = { ...filtersToUse, by_status: AWAITING_CONTROL_SURVEY, by_draftman: undefined }
    } else if (draftsmanKey === "Unassigned") {
      draftsmanFilter = { ...filtersToUse, by_draftman: undefined, unassigned: true }
    } else if (draftsmanKey === "Unassigned Overseas") {
      draftsmanFilter = { ...filtersToUse, by_status: OVERSEAS_STATE, by_draftman: undefined }
    } else {
      draftsmanFilter = { ...filtersToUse, by_draftman: draftsmanId }
    }

    getJobServicesDrafting(baseUrl, draftsmanFilter)
      .then(res => {
        if (requestId !== fetchIdRef.current) return
        if (res.status === 200) {
          const jobServices = _.get(res, "response", {})
          const matchingData = Object.values(jobServices).flat()
          setJobsDrafting(prev => ({
            ...prev,
            [draftsmanKey]: matchingData || []
          }))
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => {
        if (requestId !== fetchIdRef.current) return
        setLoadingDraftsmen(prev => ({ ...prev, [draftsmanKey]: false }))
      })
  }, [baseUrl, filters])

  const fetchData = (activeFilters = filters) => {
    fetchIdRef.current += 1
    const fetchId = fetchIdRef.current

    setJobsDrafting({})
    setExpandedDraftsmen({})
    setLoadingDraftsmen({})
    setRequesting(true)

    Promise.all([
      getDraftsmenList(baseUrl, activeFilters),
      getDraftmen(baseUrl)
    ])
      .then(([draftsmenRes, draftmenRes]) => {
        if (fetchId !== fetchIdRef.current) return

        if (draftmenRes.status === 200) {
          const newData = draftmenRes.response.map(item => {
            const { label, value, ...other } = item
            return [`${label}`, `${value}`, { ...other }]
          })
          setDraftmenOptions(newData)
        }

        if (draftsmenRes.status === 200) {
          const list = _.get(draftsmenRes, "response", [])
          setDraftsmenList(list)

          if (isManagerRole) {
            list.forEach(({ name, id, key }) => {
              if (name === "Unassigned" || name === "Unassigned Overseas") {
                const draftsmanKey = draftsmanKeyFor(key, name)
                setExpandedDraftsmen(prev => ({ ...prev, [draftsmanKey]: true }))
                fetchDataForDraftsman(draftsmanKey, id, { fetchId, filters: activeFilters })
              }
            })
          }

          const currentUserSection = list.find(({ id }) => id?.toString() === currentUser.id)
          if (currentUserSection) {
            const draftsmanKey = draftsmanKeyFor(currentUserSection.key, currentUserSection.name)
            setExpandedDraftsmen(prev => ({ ...prev, [draftsmanKey]: true }))
            fetchDataForDraftsman(draftsmanKey, currentUserSection.id, { fetchId, filters: activeFilters })
          }
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => {
        if (fetchId === fetchIdRef.current) {
          setRequesting(false)
        }
      })
  }

  const filtersRef = useRef(filters)
  const expandedDraftsmenRef = useRef(expandedDraftsmen)
  filtersRef.current = filters
  expandedDraftsmenRef.current = expandedDraftsmen

  const refreshOnVisible = useCallback(() => {
    const activeFilters = filtersRef.current
    const expanded = expandedDraftsmenRef.current
    fetchIdRef.current += 1
    const fetchId = fetchIdRef.current

    getDraftsmenList(baseUrl, activeFilters)
      .then(draftsmenRes => {
        if (fetchId !== fetchIdRef.current) return
        if (draftsmenRes.status !== 200) return

        const list = _.get(draftsmenRes, "response", [])
        setDraftsmenList(list)

        Object.entries(expanded).forEach(([draftsmanKey, isExpanded]) => {
          if (!isExpanded) return
          const draftsman = list.find(
            item => draftsmanKeyFor(item.key, item.name) === draftsmanKey
          )
          fetchDataForDraftsman(draftsmanKey, draftsman?.id, { silent: true, fetchId, filters: activeFilters })
        })
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }, [baseUrl, fetchDataForDraftsman])

  useJobServicesListRealtime({
    onUpdate: payload => {
      setJobsDrafting(prev => applyJobServiceListUrgencyUpdate(prev, payload))
    },
    onVisible: refreshOnVisible
  })

  useEffect(() => {
    getFilteredProducts(baseUrl, "drafting")
      .then(res => {
        if (res.status === 200) {
          const products = Object.values(_.get(res.response, "product", {}))
          const allJobTypes = products.map(js => ([js.attributes.name, js.id]))
          setJobTypes(allJobTypes)
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }, [])

  const handleSubmit = e => {
    e.preventDefault()
    setIsFilteredByDropDown(true)
  }

  useEffect(() => {
    const usingCustomFilters = hasCustomFilters(filtersRef.current, defaultFilters)
    const activeFilters = usingCustomFilters ? filtersRef.current : defaultFilters

    if (!usingCustomFilters) {
      setFilters(defaultFilters)
    }

    fetchData(activeFilters)
  }, [currentRole, defaultFilters])

  useEffect(() => {
    if (!isFilteredByDropDown) return

    fetchData(filters)
    setIsFilteredByDropDown(false)
  }, [isFilteredByDropDown])

  const UserBlockOutSection = useCallback(() => (
    <div className="users-block-outs-section mb-2">
      <UserBlockOutTable byTeams={teamShouldShowLeave} />
    </div>
  ), [teamShouldShowLeave])

  return (
    <Container fluid className="">
      <div className="container-inner">
        <div className="d-flex justify-content-between align-items-center header-tabs-container">
          <div className="header-tabs">
            <h1>Drafting</h1>
          </div>
        </div>
        <div className="table-listing-filters d-md-flex justify-content-between">
          <div>
            <div className="btn-group mr-2">
              <SearchInput value={filters.by_keyword} setValue={setBySearch} handleSubmit={handleSubmit} disableSubmit={requesting} />
            </div>
            {!isOverseasTeam && (
              <div className="btn-group mr-2">
                <FilterDropdown title="Status" filteredName={byStatusName} setFilter={setByStatus} options={jobStatusOptions} />
              </div>
            )}
            <div className="btn-group mr-2">
              <FilterDropdown title="Job Service" filteredName={byJobTypeName} setFilter={setByJobType} options={jobTypes} />
            </div>
            {(isOverseasManager || !readOnly) && (
              <div className="btn-group mr-2">
                <FilterDropdown title="Draftsman" filteredName={byDraftmenName} setFilter={setByDraftmen} options={draftmenOptions} />
              </div>
            )}
            <div className="btn-group">
              <AnchorButton
                className="f-small"
                onClick={clearFilters}>
                Clear Filters
              </AnchorButton>
            </div>
          </div>
        </div>

        { isManagerRole && UserBlockOutSection() }

        {requesting ? <Loader />
          : (
            <JobServiceByDraftsmanTables
              jobsDrafting={jobsDrafting}
              setJobsDrafting={setJobsDrafting}
              draftmenOptions={draftmenOptions}
              draftsmenList={draftsmenList}
              fetchDataForDraftsman={fetchDataForDraftsman}
              loadingDraftsmen={loadingDraftsmen}
              expandedDraftsmen={expandedDraftsmen}
              setExpandedDraftsmen={setExpandedDraftsmen}
              is2DTeam={is2DTeam}
              filtersApplied={filtersApplied}
              readOnly={readOnly} />
          )}
      </div>
    </Container>
  )
}


const JobServiceByDraftsmanTables = ({
  readOnly,
  filtersApplied,
  draftmenOptions,
  jobsDrafting,
  setJobsDrafting,
  draftsmenList,
  fetchDataForDraftsman,
  loadingDraftsmen,
  expandedDraftsmen,
  setExpandedDraftsmen,
  is2DTeam
}) => {
  const sortedDraftsmenList = [...draftsmenList].sort((a, b) => {
    const priority = ["Awaiting Control Survey", "Unassigned", "Unassigned Overseas"]
    const aIndex = priority.indexOf(a.name)
    const bIndex = priority.indexOf(b.name)
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
  })

  const toggleDraftsman = (draftsmanKey, draftsmanId) => {
    const isExpanded = expandedDraftsmen[draftsmanKey]
    setExpandedDraftsmen(prev => ({ ...prev, [draftsmanKey]: !isExpanded }))

    if (!isExpanded && !jobsDrafting[draftsmanKey]) {
      fetchDataForDraftsman(draftsmanKey, draftsmanId)
    }
  }

  const refetchDraftsmanData = (draftsmanKey, draftsmanId) => {
    fetchDataForDraftsman(draftsmanKey, draftsmanId)
  }

  return (
    <div className="drafting-container my-5">
      {!!sortedDraftsmenList?.length && sortedDraftsmenList.map(({
        id, name, count, key
      }) => {
        const draftsmanKey = draftsmanKeyFor(key, name)
        const isExpanded = expandedDraftsmen[draftsmanKey]
        const jobServicesDraftsman = jobsDrafting[draftsmanKey]
        const isLoading = loadingDraftsmen[draftsmanKey]

        return (
          <CollapsibleSection
            key={`draftsman-${draftsmanKey}`}
            itemKey={draftsmanKey}
            name={name}
            count={count}
            isExpanded={isExpanded}
            isLoading={isLoading}
            onToggle={() => toggleDraftsman(draftsmanKey, id)}
            onRefresh={() => refetchDraftsmanData(draftsmanKey, id)}>
            {jobServicesDraftsman?.length > 0 ? (
              <DraftingTable
                jobsDrafting={jobServicesDraftsman}
                setJobsDrafting={updatedJobs => {
                  setJobsDrafting(prev => ({
                    ...prev,
                    [draftsmanKey]: updatedJobs
                  }))
                }}
                draftmenOptions={draftmenOptions}
                fetchData={() => refetchDraftsmanData(draftsmanKey, id)}
                is2DTeam={is2DTeam}
                filtersApplied={filtersApplied}
                readOnly={readOnly} />
            ) : (
              <div className="text-center py-4 text-muted">No job services found</div>
            )}
          </CollapsibleSection>
        )
      })}
    </div>
  )
}
